'use client'

import * as React from 'react'
import Link from 'next/link'
import { AnimatePresence, motion } from 'framer-motion'
import { useLingui } from '@lingui/react'

import { Button } from '@/components/ui/button'

const KEY = 'cookie_consent'

export function CookieBanner(): React.JSX.Element {
  const { i18n } = useLingui()
  const [showBanner, setShowBanner] = React.useState<boolean>(false)

  React.useEffect(() => {
    if (!localStorage.getItem(KEY)) {
      setShowBanner(true)
    }
  }, [])

  const handleDenyCookies = (): void => {
    setShowBanner(false)
    localStorage.setItem(KEY, 'denied')
  }

  const handleAcceptCookies = (): void => {
    setShowBanner(false)
    localStorage.setItem(KEY, 'accepted')
  }

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ opacity: 0, y: 100, scale: 0.95 }}
          animate={{
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
              duration: 0.5,
              delay: 1.5,
              ease: 'easeOut'
            }
          }}
          exit={{
            opacity: 0,
            y: 100,
            scale: 0.95,
            transition: {
              duration: 0.3,
              ease: 'easeIn'
            }
          }}
          className='fixed inset-x-4 bottom-4 z-50 sm:inset-x-auto sm:left-6 sm:right-auto sm:max-w-md'
        >
          <div className='relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50/95 to-purple-50/95 dark:from-neutral-900/95 dark:to-neutral-950/95 p-6 shadow-2xl transition-all duration-300 hover:shadow-[0_0_10px_rgba(255,165,0,0.2)] border border-orange-500/10'>
            {/* Animated glow effect */}
            <span className='absolute inset-0 z-0'>
              <span className='absolute -top-12 -left-12 w-40 h-40 bg-orange-400/20 blur-3xl animate-pulse' />
              <span className='absolute -bottom-12 -right-12 w-40 h-40 bg-blue-400/20 blur-3xl animate-pulse animation-delay-300' />
            </span>
            <div className='relative z-10 space-y-5'>
              <p className='text-sm leading-relaxed text-foreground font-medium'>
                {i18n._(
                  `We use cookies mainly for analytics and to improve your experience. By accepting, you agree to our use of cookies.`
                )}{' '}
                <Link
                  href='/cookie-policy'
                  className='underline font-semibold hover:text-orange-500 transition-colors duration-200'
                >
                  {i18n._(`Learn more`)}
                </Link>
              </p>
              <div className='flex gap-3'>
                <Button
                  type='button'
                  variant='outline'
                  className='w-full rounded-lg  text-sm font-semibold'
                  onClick={handleDenyCookies}
                >
                  {i18n._(`Deny`)}
                </Button>
                <Button
                  type='button'
                  variant='default'
                  className='w-full rounded-lg text-sm font-semibold'
                  onClick={handleAcceptCookies}
                >
                  {i18n._(`Accept`)}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
